import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Bot, PhoneCall, Search, Loader2, Copy, ExternalLink } from 'lucide-react';
import BotKnowledgePanel from './BotKnowledgePanel';
import BotTestChat from '../components/BotTestChat';
import BotAppointmentsPanel from '../components/BotAppointmentsPanel';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { toast } from 'sonner';

const BotLeadsAndCalls = () => {
  const navigate = useNavigate();
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loadingBots, setLoadingBots] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [calendarError, setCalendarError] = useState('');
  const [searchLead, setSearchLead] = useState('');
  const [searchCall, setSearchCall] = useState('');
  const [appointmentsRefreshKey, setAppointmentsRefreshKey] = useState(0);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchBots = async () => {
      if (!token) return;
      setLoadingBots(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/my/bots`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to load bots');
        }
        const data = await res.json();
        setBots(data || []);
        if (data && data.length > 0) {
          setSelectedBotId(data[0].id);
        }
      } catch (err) {
        console.error(err);
        toast.error(err.message || 'Failed to load bots');
      } finally {
        setLoadingBots(false);
      }
    };

    fetchBots();
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !selectedBotId) return;
      setLoadingData(true);
      try {
        const [leadsRes, callsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bots/${selectedBotId}/leads`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bots/${selectedBotId}/voice-transcripts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const [leadsData, callsData] = await Promise.all([
          leadsRes.json().catch(() => []),
          callsRes.json().catch(() => []),
        ]);

        if (!leadsRes.ok) {
          throw new Error(leadsData.detail || 'Failed to load leads');
        }
        if (!callsRes.ok) {
          throw new Error(callsData.detail || 'Failed to load call transcripts');
        }

        setLeads(leadsData || []);
        setCalls(callsData || []);
      } catch (err) {
        console.error(err);
        toast.error(err.message || 'Failed to load data');
      } finally {
        setLoadingData(false);
      }
    };

    const fetchAppointments = async () => {
      if (!token || !selectedBotId) return;
      setLoadingAppointments(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/bots/${selectedBotId}/appointments`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json().catch(() => []);
        if (!res.ok) {
          // Likely Google Calendar not configured yet
          setCalendarError(data.detail || 'Google Calendar not configured');
          setAppointments([]);
          return;
        }
        setCalendarError('');
        setAppointments(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingAppointments(false);
      }
    };

    fetchData();
    fetchAppointments();
  }, [token, selectedBotId, appointmentsRefreshKey]);

  const selectedBotRaw = bots.find((b) => b.id === selectedBotId) || null;
  const selectedBot = (() => {
    if (!selectedBotRaw) return null;
    const botType = String(selectedBotRaw.type || 'chat').toLowerCase();
    const isChat = ['chat', 'smart', 'website'].includes(botType);
    if (!isChat || selectedBotRaw.widget_url) return selectedBotRaw;
    // Same-origin /api proxy (REACT_APP_BACKEND_URL empty) or missing PUBLIC_BACKEND_URL.
    const apiBase = (process.env.REACT_APP_BACKEND_URL || window.location.origin || '').replace(/\/$/, '');
    if (!apiBase || !selectedBotRaw.id) return selectedBotRaw;
    const widget_url = `${apiBase}/api/chatbot/${selectedBotRaw.id}.js`;
    return {
      ...selectedBotRaw,
      widget_url,
      embed_code: selectedBotRaw.embed_code || `<script src="${widget_url}" async></script>`,
    };
  })();
  const selectedIsVoice = selectedBot && String(selectedBot.type || '').toLowerCase() === 'voice';

  const filteredLeads = leads.filter((lead) => {
    if (!searchLead) return true;
    const q = searchLead.toLowerCase();
    return (
      (lead.name || '').toLowerCase().includes(q) ||
      (lead.email || '').toLowerCase().includes(q) ||
      (lead.phone || '').toLowerCase().includes(q) ||
      (lead.message || '').toLowerCase().includes(q)
    );
  });

  const filteredCalls = calls.filter((call) => {
    if (!searchCall) return true;
    const q = searchCall.toLowerCase();
    return (
      (call.from || '').toLowerCase().includes(q) ||
      (call.to || '').toLowerCase().includes(q) ||
      (call.status || '').toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const copyText = async (value, label) => {
    if (!value) {
      if (selectedIsVoice) {
        toast.error('Voice bots do not have a website widget — select a chat/smart bot, or create one in AI Tools → Smart Bot');
      } else {
        toast.error(`${label} is not available yet`);
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Bot Leads & Calls</h1>
                <p className="text-xs text-zinc-500">Track leads from Smart Bots and transcripts from Voice Bots</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-4">
        <ModuleDemoVideo moduleKey="bot-leads" className="mb-0" />
        <div className="grid grid-cols-4 gap-6">
        <section className="col-span-1 bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Bot className="w-4 h-4 text-teal-400" />
              Your Bots
            </h2>
          </div>
          {loadingBots ? (
            <div className="flex items-center justify-center h-40 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading bots...
            </div>
          ) : bots.length === 0 ? (
            <p className="text-xs text-zinc-500">No bots yet. Create one from AI Tools &gt; Bots.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {bots.map((bot) => (
                <button
                  key={bot.id}
                  onClick={() => setSelectedBotId(bot.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all ${
                    bot.id === selectedBotId
                      ? 'border-teal-500 bg-teal-500/10 text-white'
                      : 'border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium truncate">{bot.name}</span>
                    <span className="text-[10px] uppercase tracking-wide text-zinc-500">{bot.type}</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 line-clamp-2">{bot.description}</p>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="col-span-3 space-y-6">
          {selectedBot && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold">Embed this bot on a website</h2>
                  <p className="text-[11px] text-zinc-500">
                    {selectedIsVoice
                      ? 'This is a voice bot — website embed is only available for chat/smart bots. Select a chat bot from the list, or create one in AI Tools → Smart Bot.'
                      : 'Use this widget URL or script tag on any sample or live website to test chat, lead capture, and appointment flows.'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs border-zinc-700 bg-zinc-950/70"
                    onClick={() => copyText(selectedBot.widget_url, 'Widget URL')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copy Widget URL
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-3 text-xs border-zinc-700 bg-zinc-950/70"
                    onClick={() => copyText(selectedBot.embed_code, 'Embed code')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    Copy Embed Code
                  </Button>
                  {selectedBot.widget_url && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 px-3 text-xs border-zinc-700 bg-zinc-950/70"
                      onClick={() => window.open(selectedBot.widget_url, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="w-3 h-3 mr-2" />
                      Open Widget JS
                    </Button>
                  )}
                  {selectedBot.widget_url && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 px-3 text-xs border-zinc-700 bg-zinc-950/70"
                      onClick={() => window.open(`/bot-widget-sample.html?widget=${encodeURIComponent(selectedBot.widget_url)}`, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="w-3 h-3 mr-2" />
                      Open Sample Site
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-3 lg:grid-cols-2 text-xs">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">Widget URL</p>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 px-3 py-2 break-all text-zinc-200">
                    {selectedBot.widget_url || 'Widget URL unavailable'}
                  </div>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">Embed code</p>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 px-3 py-2 break-all text-zinc-200">
                    {selectedBot.embed_code || 'Embed code unavailable'}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-3 text-[11px] text-zinc-300">
                <p className="font-medium text-emerald-300 mb-1">Recommended sample website test flow</p>
                <ol className="list-decimal pl-4 space-y-1 text-zinc-400">
                  <li>Add the embed code to a simple HTML page.</li>
                  <li>Open the page and click <span className="text-zinc-200">Chat with us</span>.</li>
                  <li>Send one normal chat message first.</li>
                  <li>Use <span className="text-zinc-200">Need a callback? Leave your details</span> and submit name + email.</li>
                  <li>In the same chat session, ask to book an appointment with an exact date/time.</li>
                  <li>Check Leads and Appointments below for the result.</li>
                </ol>
              </div>
            </div>
          )}

          <BotKnowledgePanel botId={selectedBotId} token={token} />
          <BotTestChat key={selectedBotId || 'none'} botId={selectedBotId} botName={selectedBot?.name} />

          {/* Appointments + Leads + Calls */}
          <div className="grid grid-cols-2 gap-4 items-start">
            <div className="space-y-4">
              <BotAppointmentsPanel
                botId={selectedBotId}
                botName={selectedBot?.name}
                token={token}
                appointments={appointments}
                loadingAppointments={loadingAppointments}
                calendarError={calendarError}
                onRefresh={() => setAppointmentsRefreshKey((prev) => prev + 1)}
                onCreated={(app) => {
                  setAppointments((prev) => [...prev, app]);
                }}
              />

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-sm font-semibold">Leads</h2>
                    <p className="text-[11px] text-zinc-500">Captured from Smart Bot conversations</p>
                  </div>
                  <div className="relative w-64">
                    <Search className="w-3 h-3 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
                    <Input
                      value={searchLead}
                      onChange={(e) => setSearchLead(e.target.value)}
                      placeholder="Search leads..."
                      className="pl-7 h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-teal-500"
                    />
                  </div>
                </div>
            {loadingData ? (
              <div className="flex items-center justify-center h-40 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading data...
              </div>
            ) : filteredLeads.length === 0 ? (
              <p className="text-xs text-zinc-500">No leads yet for this bot.</p>
            ) : (
              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <Table className="w-full text-xs">
                    <TableHeader className="bg-zinc-900">
                      <TableRow>
                        <TableHead className="text-left px-3 py-2">Name</TableHead>
                        <TableHead className="text-left px-3 py-2">Email</TableHead>
                        <TableHead className="text-left px-3 py-2">Phone</TableHead>
                        <TableHead className="text-left px-3 py-2">Message</TableHead>
                        <TableHead className="text-left px-3 py-2">Captured At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLeads.map((lead) => (
                        <TableRow key={lead.id} className="border-t border-zinc-800">
                          <TableCell className="px-3 py-2 whitespace-nowrap">{lead.name || '-'}</TableCell>
                          <TableCell className="px-3 py-2 whitespace-nowrap">{lead.email || '-'}</TableCell>
                          <TableCell className="px-3 py-2 whitespace-nowrap">{lead.phone || '-'}</TableCell>
                          <TableCell className="px-3 py-2 max-w-xs truncate" title={lead.message || ''}>
                            {lead.message || '-'}
                          </TableCell>
                          <TableCell className="px-3 py-2 whitespace-nowrap text-zinc-500 text-[11px]">
                            {formatDate(lead.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

            </div>

            <div className="space-y-4">
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <PhoneCall className="w-4 h-4 text-cyan-400" />
                    <div>
                      <h2 className="text-sm font-semibold">Voice Call Transcripts</h2>
                      <p className="text-[11px] text-zinc-500">Inbound calls handled by your Voice Bot</p>
                    </div>
                  </div>
                  <div className="relative w-64">
                    <Search className="w-3 h-3 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
                    <Input
                      value={searchCall}
                      onChange={(e) => setSearchCall(e.target.value)}
                      placeholder="Search calls..."
                      className="pl-7 h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-cyan-500"
                    />
                  </div>
                </div>
            {loadingData ? (
              <div className="flex items-center justify-center h-40 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading data...
              </div>
            ) : filteredCalls.length === 0 ? (
              <p className="text-xs text-zinc-500">No call transcripts yet for this bot.</p>
            ) : (
              <div className="border border-zinc-800 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <Table className="w-full text-xs">
                    <TableHeader className="bg-zinc-900">
                      <TableRow>
                        <TableHead className="text-left px-3 py-2">From</TableHead>
                        <TableHead className="text-left px-3 py-2">To</TableHead>
                        <TableHead className="text-left px-3 py-2">Status</TableHead>
                        <TableHead className="text-left px-3 py-2">Last Message</TableHead>
                        <TableHead className="text-left px-3 py-2">Started At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCalls.map((call) => {
                        const messages = call.messages || [];
                        const lastBot = [...messages].reverse().find((m) => m.role === 'bot');
                        return (
                          <TableRow key={call.id || call.call_sid} className="border-t border-zinc-800 align-top">
                            <TableCell className="px-3 py-2 whitespace-nowrap">{call.from || '-'}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap">{call.to || '-'}</TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-[11px] uppercase tracking-wide text-zinc-400">
                              {call.status || 'unknown'}
                            </TableCell>
                            <TableCell className="px-3 py-2 max-w-xs text-[11px] text-zinc-300 truncate" title={lastBot?.text || ''}>
                              {lastBot?.text || '-'}
                            </TableCell>
                            <TableCell className="px-3 py-2 whitespace-nowrap text-zinc-500 text-[11px]">
                              {formatDate(call.created_at)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        </section>
        </div>
      </main>
    </div>
  );
};

export default BotLeadsAndCalls;
